import { FormatPageStructureEditor, IEditableFormatObject, IFormatObjectEditable } from "../editableFormat";
import { instanceOfIQuestionnairePropertyEditable } from "../editableFormat/properties";
import { ObjectUtility } from "../../../base/utilities";
import { instanceOfIQuestionnaireSpecifiable, IQuestionnaire } from "../../../documaker/domain/format/objects";

export class QuestionnaireGuidTree<T extends IFormatObjectEditable | IEditableFormatObject> {
  constructor(object: T) {
    this.guid = QuestionnaireGuidTree.toQuestionnaireGuid(object);
    this.childGuids = QuestionnaireGuidTree.toQuestionnaireChildGuids(object);
    this.source = object;
  }
  level: number = 0;
  parentGuid: string = null;
  guid: string = null;
  childGuids: string[] = [];
  parent: QuestionnaireGuidTree<T> = null;
  source: T = null;
  children: QuestionnaireGuidTree<T>[] = [];

  containParent(guid: string): boolean {
    if (!this.parent) {
      return false;
    } else if (this.parentGuid === guid) {
      return true;
    } else {
      return this.parent.containParent(guid);
    }
  }

  containChildren(guid: string): boolean {
    return this.children.some(c => c.guid === guid || c.containChildren(guid));
  }

  toChildren(): QuestionnaireGuidTree<T>[] {
    return this.children.reduce((p, c) => p.concat([c], (c.children.length === 0) ? [] : c.toChildren()), [] as QuestionnaireGuidTree<T>[]);
  }

  updateChildren(childGuids: string[], items: QuestionnaireGuidTree<T>[]) {
    this.children.forEach(c => {
      c.updateLevel(0);
      c.parentGuid = null;
      c.parent = null;
    });
    this.childGuids = childGuids;
    this.children = QuestionnaireGuidTree.toQuestionnaireGuidTreeChildren(this, items);
  }

  private updateLevel(level: number) {
    this.level = level;
    this.children.forEach(c => c.updateLevel(level + 1));
  }

  static from<T extends IFormatObjectEditable | IEditableFormatObject>(objects: T[]): QuestionnaireGuidTree<T>[] {
    const items = objects.map(o => new QuestionnaireGuidTree(o));
    items.forEach(v => v.children = QuestionnaireGuidTree.toQuestionnaireGuidTreeChildren(v, items));
    return items;
  }

  static sort<T extends IFormatObjectEditable | IEditableFormatObject>(items: QuestionnaireGuidTree<T>[]): QuestionnaireGuidTree<T>[] {
    const results: QuestionnaireGuidTree<T>[] = [];
    this.sortByGuidTree(results, items.filter(v => !v.parentGuid));
    return results;
  }

  private static sortByGuidTree<T extends IFormatObjectEditable | IEditableFormatObject>(results: QuestionnaireGuidTree<T>[], targets: QuestionnaireGuidTree<T>[]) {
    targets.forEach(v => {
      results.push(v);
      this.sortByGuidTree(results, v.children);
    });
  }

  private static toQuestionnaireGuid<T>(object: T): string {
    return instanceOfIQuestionnairePropertyEditable(object)
      ? object.questionnaire().getValue()._guid
      : instanceOfIQuestionnaireSpecifiable(object)
        ? object.questionnaire._guid
        : null;
  }

  private static toQuestionnaireChildGuids<T>(object: T): string[] {
    return instanceOfIQuestionnairePropertyEditable(object)
      ? object.questionnaire().getValue()._childGuids
      : instanceOfIQuestionnaireSpecifiable(object)
        ? object.questionnaire._childGuids
        : [];
  }

  private static toQuestionnaireGuidTreeChildren<T extends IFormatObjectEditable | IEditableFormatObject>(source: QuestionnaireGuidTree<T>, items: QuestionnaireGuidTree<T>[]): QuestionnaireGuidTree<T>[] {
    return source.childGuids.map(g => {
      const item = items.find(q => q.guid === g);
      if (!item || item.containParent(g)) {
        return null;
      }
      item.level = source.level + 1;
      item.parentGuid = source.guid;
      item.parent = source;
      item.children = this.toQuestionnaireGuidTreeChildren(item, items);
      return item;
    }).filter(q => !!q);
  }
}

export class QuestionnaireGuidTreeEditor {
  constructor(private source: FormatPageStructureEditor) {
    this.objects = source.getObjects();
    this.items = QuestionnaireGuidTree.from(this.objects);
  }
  objects: IFormatObjectEditable[] = [];
  items: QuestionnaireGuidTree<IFormatObjectEditable>[] = [];

  private selectedGuidTreeItems(): QuestionnaireGuidTree<IFormatObjectEditable>[] {
    return this.items.filter(a => a.source.isSelected());
  }

  insertSelectedObjects(index: number, isHitObject: boolean, isNextObject: boolean) {
    this.removeUnnecessaryQuestionnaireChildGuids();
    const selectedGuidTreeItems = this.selectedGuidTreeItems().filter(s => !s.parent);
    if (isHitObject) {
      this.insertSelectedObjectsAsChildren(index, selectedGuidTreeItems);
    } else if (isNextObject) {
      this.insertSelectedObjectsAsNextSibling(index, selectedGuidTreeItems);
    } else {
      this.insertSelectedObjectsAsPreviousSibling(index, selectedGuidTreeItems);
    }
    this.sortObjects();
  }

  private insertSelectedObjectsAsChildren(index: number, selectedGuidTreeItems: QuestionnaireGuidTree<IFormatObjectEditable>[]) {
    const item = this.items[index];
    this.updateQuestionnaireChildGuids(item, item.children.concat(selectedGuidTreeItems));
  }

  private insertSelectedObjectsAsNextSibling(index: number, selectedGuidTreeItems: QuestionnaireGuidTree<IFormatObjectEditable>[]) {
    const item = this.items[index];
    if (!item) {
      // indexが範囲外の場合は末尾に追加
      // 親が無いため、並び替えに必要なitems自体を更新する
      const selectedGuid = selectedGuidTreeItems.map(i => i.guid);
      const prev = this.items.filter(i => selectedGuid.every(g => g !== i.guid));
      this.items = prev.concat(selectedGuidTreeItems);
      return;
    }
    if (item.parent) {
      // 親要素の子要素として追加
      // 親のchildGuidsを更新することで並び替えに必要な情報を書き換える
      const parentIndex = item.parent.children.findIndex(c => c.guid === item.guid);
      const prev = item.parent.children.slice(0, parentIndex + 1);
      const next = item.parent.children.slice(parentIndex + 1, item.parent.children.length);
      const merged = prev.concat(selectedGuidTreeItems, next);
      this.updateQuestionnaireChildGuids(item.parent, merged);
    } else {
      // ルート要素として追加
      // 親が無いため、並び替えに必要なitems自体を更新する
      const selectedGuid = selectedGuidTreeItems.map(i => i.guid);
      const prev = this.items.slice(0, index + 1).filter(i => selectedGuid.every(g => g !== i.guid));
      const next = this.items.slice(index + 1, this.items.length).filter(i => selectedGuid.every(g => g !== i.guid));
      this.items = prev.concat(selectedGuidTreeItems, next);
    }
  }

  private insertSelectedObjectsAsPreviousSibling(index: number, selectedGuidTreeItems: QuestionnaireGuidTree<IFormatObjectEditable>[]) {
    const item = this.items[index];
    if (!item) {
      // indexが範囲外の場合は末尾に追加
      // 親が無いため、並び替えに必要なitems自体を更新する
      const selectedGuid = selectedGuidTreeItems.map(i => i.guid);
      const prev = this.items.filter(i => selectedGuid.every(g => g !== i.guid));
      this.items = prev.concat(selectedGuidTreeItems);
      return;
    }
    if (item.parent) {
      // 親要素の子要素として追加
      // 親のchildGuidsを更新することで並び替えに必要な情報を書き換える
      const parentIndex = item.parent.children.findIndex(c => c.guid === item.guid);
      const prev = item.parent.children.slice(0, parentIndex);
      const next = item.parent.children.slice(parentIndex, item.parent.children.length);
      const merged = prev.concat(selectedGuidTreeItems, next);
      this.updateQuestionnaireChildGuids(item.parent, merged);
    } else {
      // ルート要素として追加
      // 親が無いため、並び替えに必要なitems自体を更新する
      const selectedGuid = selectedGuidTreeItems.map(i => i.guid);
      const prev = this.items.slice(0, index).filter(i => selectedGuid.every(g => g !== i.guid));
      const next = this.items.slice(index, this.items.length).filter(i => selectedGuid.every(g => g !== i.guid));
      this.items = prev.concat(selectedGuidTreeItems, next);
    }
  }

  private removeUnnecessaryQuestionnaireChildGuids() {
    // 選択したオブジェクトに含まれている不要な子アイテムを削除
    const selectedGuids = this.selectedGuidTreeItems().map(s => s.guid);
    this.items.forEach(i => {
      if (i.childGuids.length === 0 || selectedGuids.some(g => g === i.guid)) {
        return;
      }
      const childGuidItems = i.children.filter(c => selectedGuids.every(g => g !== c.guid));
      if (childGuidItems.length < i.children.length) {
        this.updateQuestionnaireChildGuids(i, childGuidItems);
      }
    });
  }

  private updateQuestionnaireChildGuids(item: QuestionnaireGuidTree<IFormatObjectEditable>, childGuidItems: QuestionnaireGuidTree<IFormatObjectEditable>[]) {
    if (!instanceOfIQuestionnairePropertyEditable(item.source)) {
      return;
    }
    const clone = ObjectUtility.copyDeeply(item.source.questionnaire().getValue()) as IQuestionnaire;
    const childGuids = childGuidItems.map(o => o.guid).filter(o => !!o);
    clone._childGuids = childGuids;
    item.source.questionnaire().setValue(clone);
    this.source.updateObject(item.source);
    // 管理ツリーを更新
    item.updateChildren(childGuids, this.items);
  }

  private sortObjects() {
    const rebuildedObjects = this.rebuildedObjects();

    // 並び替え対象オブジェクトの期待順序を表す source → index のマップを事前に構築する
    const sourceOrderMap = new Map<IEditableFormatObject, number>();
    rebuildedObjects.forEach((obj, index) => {
      sourceOrderMap.set(obj.getSource(), index);
    });

    this.source.sortObjects((a, b) => {
      const indexA = sourceOrderMap.get(a);
      const indexB = sourceOrderMap.get(b);
      // マップに存在しない場合は末尾扱いとし、undefined による NaN を避ける
      const safeIndexA = indexA !== undefined ? indexA : Number.MAX_SAFE_INTEGER;
      const safeIndexB = indexB !== undefined ? indexB : Number.MAX_SAFE_INTEGER;
      return safeIndexA - safeIndexB;
    });
  }

  private rebuildedObjects(): IFormatObjectEditable[] {
    // 現在のツリー情報で再構築されたオブジェクト一覧を返す
    const rebuildedObjects: IFormatObjectEditable[] = [];
    const rebuildItems = QuestionnaireGuidTree.sort(this.items.filter(i => !i.parent));
    rebuildItems.forEach(i => {
      rebuildedObjects.push(i.source);
      rebuildedObjects.push(...i.toChildren().map(c => c.source));
    });
    return rebuildedObjects;
  }
}
